// Recebimento em LOTE: vários títulos da mesma família em UM único recebimento.
// Regra: parte SEMPRE do valor nominal em aberto — o desconto disponível é opcional, título por título.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, CheckCircle2, Layers, Loader2, Receipt, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { SplitPaymentLines, lineBody, lineFee } from './SplitPaymentLines';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const METHODS = [['pix', 'PIX'], ['cash', 'Dinheiro'], ['card_debit', 'Cartão Débito'],
  ['card_credit', 'Cartão Crédito'], ['pix_card', 'PIX no Cartão']];
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const pct = (v) => String(Number(v) || 0).replace('.', ',');
const brDate = (s) => (String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—');
// aceita "5%" (percentual) ou "3,75" / "3.75" (valor em R$) digitado direto na linha
const toNum = (s) => {
  const t = String(s ?? '').replace('%', '').trim();
  const n = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  return Number(n.replace(/[^\d.]/g, '')) || 0;
};
const parseDesc = (txt, nominal) => {
  const s = String(txt ?? '').trim();
  if (!s) return 0;
  const v = toNum(s);
  return s.includes('%') ? r2(nominal * Math.min(v, 100) / 100) : Math.min(r2(v), r2(nominal));
};

export default function BatchSettlementModal({ open, onOpenChange, titleIds = [], onSettled }) {
  const [snap, setSnap] = useState(null);
  const [method, setMethod] = useState('pix');
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [quote, setQuote] = useState(null);
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [valor, setValor] = useState('');
  const [form, setForm] = useState({ received_amount: '', installments: 1, brand: '', last4: '', nsu: '', authorization_code: '', acquirer: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [ajuste, setAjuste] = useState(false);
  const [manualAlloc, setManualAlloc] = useState({});
  const [desc, setDesc] = useState({});                // {title_id: '5%' | '3,75'}
  const [descTodos, setDescTodos] = useState('');      // atalho: mesmo desconto em todos
  const [applyFee, setApplyFee] = useState({});        // {title_id: true} multa
  const [applyInt, setApplyInt] = useState({});        // {title_id: true} juros
  const [manualInt, setManualInt] = useState({});      // {title_id: {type, value, reason}}
  const [motivo, setMotivo] = useState('');
  const [extras, setExtras] = useState([]);         // formas adicionais do MESMO recebimento
  const isCard = ['card_debit', 'card_credit', 'pix_card'].includes(method);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const erro = (e, fb) => {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === 'string' ? d : fb);
  };

  const rawItems = snap?.items || [];
  const cfg = snap?.late_charge_config || { late_fee_percent: 2, daily_interest_percent: 0.0333, grace_days: 0 };
  const diasDe = (due) => {
    const d = String(due || '').slice(0, 10);
    if (!d || !paidAt) return 0;
    return Math.max(0, Math.round((new Date(`${paidAt}T00:00:00`) - new Date(`${d}T00:00:00`)) / 86400000));
  };

  // cálculo por título: nominal → desconto opcional → liquidação → encargos opcionais → a receber
  const items = useMemo(() => rawItems.map((it) => {
    const nominal = r2(it.nominal_amount ?? it.original_amount);
    const consolidated = !!it.discount_consolidated;
    const available = r2(consolidated ? 0 : it.discount_available);
    const availablePct = r2(it.discount_available_percent);
    const nominalBalance = r2(it.nominal_balance ?? Math.max(0, nominal - r2(it.received)));
    const txt = desc[it.title_id];
    const typed = parseDesc(txt, nominalBalance);
    const applied = consolidated ? r2(it.discount_amount) : typed;
    const isAuto = consolidated || (available > 0.009 && Math.abs(applied - available) <= 0.005);
    const isManual = !consolidated && applied > 0.009 && !isAuto;
    const base = r2(Math.max(0, nominal - applied - r2(it.received)));
    const dias = diasDe(it.due_date);
    const diasJuros = dias;
    const multaCalc = dias > 0 ? r2(base * Number(cfg.late_fee_percent || 0) / 100) : 0;
    const jurosCalc = dias > 0 ? r2(base * Number(cfg.daily_interest_percent || 0) * diasJuros / 100) : 0;
    const multa = applyFee[it.title_id] ? multaCalc : 0;
    const mi = manualInt[it.title_id];
    const jurosManual = mi
      ? (mi.type === 'percent'
        ? Math.min(r2(base * Math.min(Number(mi.value || 0), 100) / 100), base)
        : Math.min(Math.max(r2(mi.value), 0), base))
      : 0;
    const juros = applyInt[it.title_id] ? (mi ? jurosManual : jurosCalc) : 0;
    const receber = r2(base + multa + juros);
    return { ...it, nominal, consolidated, available, availablePct, applied, isAuto, isManual,
      descTxt: txt ?? '', base,
      dias, diasJuros, multaCalc, jurosCalc, multa, juros, receber, manualInterest: mi || null,
      nominalBalance };
  }), [rawItems, desc, applyFee, applyInt, manualInt, paidAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalNominal = r2(items.reduce((a, b) => a + b.nominalBalance, 0));
  const totalDesc = r2(items.reduce((a, b) => a + b.applied, 0));
  const totalMulta = r2(items.reduce((a, b) => a + b.multa, 0));
  const totalJuros = r2(items.reduce((a, b) => a + b.juros, 0));
  const totalReceber = r2(items.reduce((a, b) => a + b.receber, 0));
  const vencidos = items.filter((i) => i.dias > 0);
  const temManual = items.some((i) => i.isManual);
  const split = extras.length > 0;
  const extrasSum = r2(extras.reduce((s, l) => s + r2(l.amount), 0));
  const line1Gross = split
    ? (valor === '' ? Math.max(0, r2(totalReceber - extrasSum)) : Math.max(0, r2(valor)))
    : (valor === '' ? totalReceber : Math.min(Number(valor || 0), totalReceber));
  const gross = split ? r2(line1Gross + extrasSum) : line1Gross;
  const splitOver = split && gross - totalReceber > 0.009;
  const falta = r2(totalReceber - gross);
  const parcial = gross > 0 && falta > 0.009;
  const fee1 = quote?.fee_amount || 0;
  const fee = r2(fee1 + extras.reduce((s, l) => s + lineFee(l), 0));

  // distribuição: vencimento mais antigo primeiro (espelha o backend) ou ajuste manual
  const auto = useMemo(() => {
    const out = {};
    let restante = gross;
    items.forEach((it) => {
      const v = restante <= 0.009 ? 0 : Math.min(r2(restante), it.receber);
      out[it.title_id] = v;
      restante = r2(restante - v);
    });
    return out;
  }, [items, gross]);

  const dist = ajuste ? manualAlloc : auto;
  const distTotal = r2(Object.values(dist).reduce((a, b) => a + Number(b || 0), 0));
  const distOk = Math.abs(distTotal - gross) <= 0.009;

  const load = useCallback(async () => {
    if (!open || !titleIds.length) return;
    setSnap(null); setDone(null); setValor(''); setAjuste(false); setManualAlloc({}); setExtras([]);
    setDesc({}); setDescTodos(''); setMotivo('');
    setApplyFee({}); setApplyInt({}); setManualInt({});
    try {
      const { data } = await api.post('/financial/titulos/batch-quote', { title_ids: titleIds, paid_at: paidAt });
      setSnap(data);
      // já traz o desconto cadastrado de cada título na linha (editável/removível)
      setDesc(Object.fromEntries((data.items || [])
        .filter((i) => !i.discount_consolidated && r2(i.discount_available) > 0.009)
        .map((i) => [i.title_id, String(r2(i.discount_available)).replace('.', ',')])));
    } catch (e) {
      erro(e, 'Não foi possível carregar os títulos selecionados.');
      setSnap({ items: [], blocked: [], total_balance: 0, family_ok: true });
    }
  }, [open, JSON.stringify(titleIds)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/financial/accounts/receiving', { params: { payment_method: method } });
        setAccounts(data.accounts || []);
        setAccountId((prev) => ((data.accounts || []).some((a) => a.id === prev) ? prev
          : ((data.accounts || []).find((a) => a.padrao) || (data.accounts || [])[0] || {}).id || ''));
      } catch (_) { setAccounts([]); }
    })();
  }, [method, open]);

  useEffect(() => {
    if (!accountId || !line1Gross) { setQuote(null); return; }
    (async () => {
      try {
        const { data } = await api.get('/financial/accounts/fee-quote', {
          params: { account_id: accountId, payment_method: method, installments: Number(form.installments || 1), amount: line1Gross },
        });
        setQuote(data);
      } catch (_) { setQuote(null); }
    })();
  }, [accountId, method, form.installments, line1Gross]);

  const aplicarEmTodos = () => {
    const txt = String(descTodos || '').trim();
    if (!txt) { toast.error('Digite o desconto (ex.: 5% ou 3,75) para aplicar em todos.'); return; }
    setDesc(Object.fromEntries(items.filter((i) => !i.consolidated).map((i) => [i.title_id, txt])));
    setValor(''); setAjuste(false);
  };

  const limparDescontos = () => { setDesc({}); setDescTodos(''); setValor(''); setAjuste(false); };

  const encargosTodos = (on) => {
    const next = {};
    items.forEach((it) => { if (it.dias > 0) next[it.title_id] = on; });
    setApplyFee(next); setApplyInt({ ...next }); setValor(''); setAjuste(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/financial/titulos/batch-settle', {
        title_ids: items.map((i) => i.title_id),
        payment_method: method, account_id: accountId || null, paid_at: paidAt,
        amount: gross,
        allocations: ajuste ? Object.fromEntries(Object.entries(dist)
          .map(([k, v]) => [k, Number(v || 0)]).filter(([, v]) => v > 0)) : null,
        apply_discount: Object.fromEntries(items.map((i) => [i.title_id, !!i.isAuto])),
        charges: Object.fromEntries(items.map((i) => [i.title_id, {
          late_fee: !!applyFee[i.title_id] && i.dias > 0,
          interest: !!applyInt[i.title_id] && (i.dias > 0 || !!i.manualInterest),
          interest_mode: i.manualInterest ? 'manual' : 'auto',
          interest_type: i.manualInterest?.type || 'amount',
          interest_value: i.manualInterest ? Number(i.manualInterest.value || 0) : null,
          interest_reason: i.manualInterest?.reason || motivo || null,
        }])),
        final_amounts: null,
        discounts: temManual
          ? Object.fromEntries(items.filter((i) => i.isManual).map((i) => [i.title_id, i.applied]))
          : null,
        discount_reason: temManual ? motivo : null,
        installments: ['card_credit', 'pix_card'].includes(method) ? Number(form.installments || 1) : 1,
        brand: isCard ? form.brand || null : null,
        last4: isCard ? form.last4 || null : null,
        nsu: isCard ? form.nsu || null : null,
        authorization_code: isCard ? form.authorization_code || null : null,
        acquirer: isCard ? form.acquirer || null : null,
        received_amount: method === 'cash' && form.received_amount ? Number(form.received_amount) : null,
        notes: form.notes || null,
        splits: split ? [
          { payment_method: method, amount: line1Gross, account_id: accountId || null,
            installments: ['card_credit', 'pix_card'].includes(method) ? Number(form.installments || 1) : 1,
            brand: isCard ? form.brand || null : null, last4: isCard ? form.last4 || null : null,
            nsu: isCard ? form.nsu || null : null, authorization_code: isCard ? form.authorization_code || null : null,
            acquirer: isCard ? form.acquirer || null : null,
            received_amount: method === 'cash' && form.received_amount ? Number(form.received_amount) : null },
          ...extras.map(lineBody),
        ] : null,
      });
      setDone(data);
      toast.success(`${data.count} título(s) baixados em um único recebimento de ${brl(data.gross_amount)}.`);
      onSettled?.(data);
    } catch (e) { erro(e, 'Erro ao registrar o recebimento em lote.'); }
    finally { setBusy(false); }
  };

  const verRecibo = async () => {
    try {
      const r = await api.get(`/financial/recibo-lote/${done.batch_id}/recibo.jpg`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) { erro(e, 'Não foi possível gerar o recibo do lote.'); }
  };

  const enviarRecibo = async () => {
    try {
      const { data } = await api.get(`/financial/recibo-lote/${done.batch_id}/share`);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.image_path}`;
      const msg = encodeURIComponent(`${data.text}${url}`);
      window.open(data.phone ? `https://wa.me/${data.phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
      toast.success(data.phone ? `Recibo pronto para ${data.payer_name || 'o responsável'}.`
        : 'Responsável sem telefone: escolha o contato no WhatsApp.');
    } catch (e) { erro(e, 'Não foi possível preparar o recibo do lote.'); }
  };

  const bloqueado = busy || splitOver || !distOk || !snap?.family_ok || items.length < 2 || gross <= 0
    || (temManual && !motivo.trim())
    || items.some((i) => applyInt[i.title_id] && i.manualInterest
      && !String(i.manualInterest.reason || '').trim() && !motivo.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1180px] w-[calc(100vw-1.5rem)] sm:w-[95vw] max-h-[92vh] overflow-y-auto overflow-x-hidden"
        data-testid="batch-settlement-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-violet-600" /> Registrar pagamento
          </DialogTitle>
          <DialogDescription>
            {done ? 'Pagamento registrado — emita o recibo único do recebimento.'
              : `${(snap?.students || []).join(', ') || 'Família'}${snap?.payer?.name ? ` · Responsável: ${snap.payer.name}` : ''} · ${snap?.count || 0} título(s) selecionado(s)`}
          </DialogDescription>
        </DialogHeader>

        {!snap ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="batch-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Carregando títulos…
          </p>
        ) : done ? (
          <div className="space-y-3" data-testid="batch-done">
            <div className="rounded-lg border bg-emerald-50 p-3 text-sm">
              <p className="flex items-center gap-2 font-semibold text-emerald-800">
                <CheckCircle2 className="w-4 h-4" /> {done.count} título(s) baixados · {brl(done.gross_amount)}
              </p>
              <p className="text-[12px] text-emerald-900/80 mt-1">
                Nominal {brl(done.nominal_total)} · descontos {brl(done.discount_total)}
                {done.late_fee_total > 0 ? ` · multa ${brl(done.late_fee_total)}` : ''}
                {done.interest_total > 0 ? ` · juros ${brl(done.interest_total)}` : ''}
                {' '}· taxa {brl(done.fee_amount)} · líquido {brl(done.net_amount)}
                {done.partial_count > 0 ? ` · ${done.partial_count} título(s) ficaram parciais` : ''}
              </p>
            </div>
            <div className="rounded-lg border divide-y text-sm">
              {(done.items || []).map((i) => (
                <div key={i.title_id} className="flex justify-between gap-2 px-3 py-2" data-testid={`batch-done-${i.title_id}`}>
                  <span className="truncate">
                    <b className="text-slate-800">{i.student_name}</b> · {i.description}
                    {i.discount_applied > 0 && (
                      <em className="not-italic text-emerald-700"> · desconto {brl(i.discount_applied)}</em>
                    )}
                  </span>
                  <span className="whitespace-nowrap font-semibold">
                    {brl(i.paid_amount)}{i.partial ? <em className="text-amber-700 not-italic"> · saldo {brl(i.balance_after)}</em> : ''}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={verRecibo} data-testid="batch-receipt-view">
                <Receipt className="w-4 h-4 mr-2" /> Recibo em JPEG
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={enviarRecibo} data-testid="batch-receipt-wa">
                <Send className="w-4 h-4 mr-2" /> Enviar no WhatsApp
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="batch-close">Concluir</Button>
            </div>
          </div>
        ) : (
          <>
            {!snap.family_ok && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12.5px] text-rose-800 flex gap-2"
                data-testid="batch-family-error">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Para registrar um único pagamento, selecione títulos do mesmo aluno (ou de irmãos da mesma família).
              </p>
            )}
            {snap.family_ok && items.length < 2 && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-800 flex gap-2"
                data-testid="batch-min-hint">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Selecione ao menos dois títulos com saldo em aberto para registrar um único pagamento.
              </p>
            )}
            {(snap.blocked || []).length > 0 && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800"
                data-testid="batch-blocked">
                {snap.blocked.length} título(s) fora do lote: {snap.blocked.map((b) => `${b.description || b.title_id} (${b.reason})`).join(' · ')}
              </p>
            )}

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
              {/* ── TÍTULOS ── */}
              <div className="space-y-2 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[11.5px] font-semibold uppercase tracking-wide text-slate-500">Títulos</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Input className="h-7 w-24 text-[11px]" placeholder="5% ou 3,75"
                      value={descTodos} data-testid="batch-discount-all-input"
                      onChange={(e) => setDescTodos(e.target.value)} />
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                      data-testid="batch-apply-all-discounts" onClick={aplicarEmTodos}>
                      Aplicar mesmo desconto em todos
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]"
                      data-testid="batch-clear-all-discounts" onClick={limparDescontos}>
                      Limpar descontos
                    </Button>
                    {vencidos.length > 0 && (
                      <>
                        <Button type="button" size="sm" variant="outline"
                          className="h-7 text-[11px] border-amber-300 text-amber-800"
                          data-testid="batch-apply-all-charges" onClick={() => encargosTodos(true)}>
                          Encargos nos vencidos ({vencidos.length})
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px] text-amber-800"
                          data-testid="batch-clear-all-charges" onClick={() => encargosTodos(false)}>
                          Remover encargos
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-2 lg:max-h-[46vh] lg:overflow-y-auto lg:pr-1" data-testid="batch-items">
                  {items.map((it) => (
                    <div key={it.title_id} className="rounded-xl border p-3 text-[13px]"
                      data-testid={`batch-item-${it.title_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{it.description}</p>
                          <p className="text-[11.5px] text-slate-500">
                            {it.student_name} · Vencimento: {brDate(it.due_date)}
                            {it.dias > 0 && (
                              <em className="not-italic font-semibold text-amber-700"> · {it.dias} dia(s) em atraso</em>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="mt-2 space-y-1">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                          <div className="min-w-[96px]">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Nominal</p>
                            <b className="text-[13.5px]" data-testid={`batch-item-nominal-${it.title_id}`}>
                              {brl(it.nominalBalance)}
                            </b>
                          </div>
                          <div className="min-w-[124px]">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Desconto</p>
                            {it.consolidated ? (
                              <b className="text-[13px] text-emerald-700"
                                data-testid={`batch-item-consolidated-${it.title_id}`}>
                                {brl(it.discount_amount)} <span className="font-normal text-[10.5px]">(já aplicado)</span>
                              </b>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <Input className="h-8 w-[84px] text-right text-[12.5px]"
                                  placeholder="5% ou 3,75" value={it.descTxt}
                                  data-testid={`batch-item-discount-${it.title_id}`}
                                  onChange={(e) => { setDesc((v) => ({ ...v, [it.title_id]: e.target.value }));
                                    setValor(''); setAjuste(false); }} />
                                <span className={`text-[11px] font-semibold ${it.isManual ? 'text-amber-700' : 'text-emerald-700'}`}
                                  data-testid={`batch-item-discount-amount-${it.title_id}`}>
                                  {it.applied > 0.009 ? `- ${brl(it.applied)}` : '—'}
                                  {it.isManual ? ' manual' : ''}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-auto text-right">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">A receber</p>
                            {ajuste ? (
                              <Input type="number" step="0.01" min="0" max={it.receber}
                                className="h-8 w-28 text-right text-[12.5px]"
                                data-testid={`batch-alloc-${it.title_id}`}
                                value={manualAlloc[it.title_id] ?? 0}
                                onChange={(e) => setManualAlloc((v) => ({ ...v, [it.title_id]: e.target.value }))} />
                            ) : (
                              <b className="text-[14.5px]" data-testid={`batch-item-receber-${it.title_id}`}>
                                {brl(dist[it.title_id] ?? it.receber)}
                                {(dist[it.title_id] ?? it.receber) + 0.009 < it.receber && (
                                  <em className="not-italic text-[11.5px] text-amber-700"> /{brl(it.receber)}</em>
                                )}
                              </b>
                            )}
                          </div>
                        </div>
                        {!it.consolidated && it.available > 0.009 && (
                          <p className="text-[10.5px] text-slate-400"
                            data-testid={`batch-item-available-${it.title_id}`}>
                            Desconto cadastrado: {brl(it.available)}{it.availablePct ? ` (${it.availablePct}%)` : ''}
                          </p>
                        )}

                        {it.dias > 0 && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-1"
                            data-testid={`batch-item-charges-${it.title_id}`}>
                            <p className="text-[11px] font-bold text-amber-900">
                              Atraso de {it.dias} dia(s) · liquidação {brl(it.base)}
                            </p>
                            <label className="flex items-center justify-between gap-2 text-[11.5px] font-semibold text-amber-900 cursor-pointer">
                              <span className="flex items-center gap-1.5">
                                <input type="checkbox" checked={!!applyFee[it.title_id]}
                                  data-testid={`batch-item-fee-${it.title_id}`}
                                  onChange={(e) => { setApplyFee((v) => ({ ...v, [it.title_id]: e.target.checked })); setValor(''); setAjuste(false); }} />
                                Multa {pct(cfg.late_fee_percent)}%
                              </span>
                              <span>{brl(it.multaCalc)}</span>
                            </label>
                            <label className="flex items-center justify-between gap-2 text-[11.5px] font-semibold text-amber-900 cursor-pointer">
                              <span className="flex items-center gap-1.5">
                                <input type="checkbox" checked={!!applyInt[it.title_id]}
                                  data-testid={`batch-item-interest-${it.title_id}`}
                                  onChange={(e) => { setApplyInt((v) => ({ ...v, [it.title_id]: e.target.checked })); setValor(''); setAjuste(false); }} />
                                Juros {it.manualInterest ? 'manual' : `${pct(cfg.daily_interest_percent)}%/dia (${it.diasJuros}d)`}
                              </span>
                              <span>{brl(it.manualInterest ? it.juros : it.jurosCalc)}</span>
                            </label>
                            {applyInt[it.title_id] && (
                              <div className="pl-5 space-y-1">
                                <div className="flex gap-1">
                                  {[['auto', 'Automático'], ['manual', 'Manual']].map(([k, lb]) => (
                                    <button key={k} type="button"
                                      className={`px-2 py-0.5 rounded text-[10.5px] font-semibold border ${
                                        (it.manualInterest ? 'manual' : 'auto') === k
                                          ? 'bg-amber-600 text-white border-amber-600'
                                          : 'bg-white text-amber-900 border-amber-300'}`}
                                      data-testid={`batch-item-interest-mode-${k}-${it.title_id}`}
                                      onClick={() => {
                                        setManualInt((v) => ({ ...v, [it.title_id]: k === 'manual' ? { type: 'amount', value: '', reason: '' } : undefined }));
                                        setValor(''); setAjuste(false);
                                      }}>{lb}</button>
                                  ))}
                                </div>
                                {it.manualInterest && (
                                  <div className="space-y-1">
                                    <div className="flex gap-1">
                                      <select className="h-7 rounded border px-1 text-[11px]"
                                        value={it.manualInterest.type}
                                        data-testid={`batch-item-interest-type-${it.title_id}`}
                                        onChange={(e) => { setManualInt((v) => ({ ...v, [it.title_id]: { ...v[it.title_id], type: e.target.value } })); setValor(''); }}>
                                        <option value="amount">R$</option>
                                        <option value="percent">%</option>
                                      </select>
                                      <Input type="number" step="0.01" min="0" className="h-7 text-[11px]"
                                        placeholder="Juros deste título" value={it.manualInterest.value}
                                        data-testid={`batch-item-interest-value-${it.title_id}`}
                                        onChange={(e) => { setManualInt((v) => ({ ...v, [it.title_id]: { ...v[it.title_id], value: e.target.value } })); setValor(''); setAjuste(false); }} />
                                    </div>
                                    <Input className="h-7 text-[11px]" placeholder="Motivo do juros manual (obrigatório)"
                                      value={it.manualInterest.reason}
                                      data-testid={`batch-item-interest-reason-${it.title_id}`}
                                      onChange={(e) => setManualInt((v) => ({ ...v, [it.title_id]: { ...v[it.title_id], reason: e.target.value } }))} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex items-center justify-between border-t pt-1 text-[11.5px] text-slate-500">
                          <span>Total do título</span>
                          <span data-testid={`batch-item-total-${it.title_id}`}>{brl(it.receber)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {temManual && (
                  <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Motivo do desconto manual do lote (obrigatório)" className="h-9 text-[12.5px]"
                    data-testid="batch-desconto-motivo" />
                )}
                {temManual && !motivo.trim() && (
                  <p className="text-[11.5px] font-semibold text-rose-700" data-testid="batch-desconto-erro">
                    Informe o motivo do desconto manual.
                  </p>
                )}
              </div>

              {/* ── RESUMO ── */}
              <div className="space-y-2 lg:sticky lg:top-0 lg:self-start" data-testid="batch-summary">
                <div className="rounded-xl border bg-slate-50 p-3 text-[13px] space-y-1">
                  <p className="text-[11.5px] font-semibold uppercase tracking-wide text-slate-500">Resumo</p>
                  <div className="flex justify-between"><span>{items.length} título(s)</span></div>
                  <div className="flex justify-between">
                    <span>Total nominal</span><b data-testid="batch-total-nominal">{brl(totalNominal)}</b>
                  </div>
                  <div className="flex justify-between text-emerald-700">
                    <span>Descontos aplicados</span><b data-testid="batch-total-desconto">- {brl(totalDesc)}</b>
                  </div>
                  {totalMulta > 0 && (
                    <div className="flex justify-between text-amber-800">
                      <span>Multa de atraso</span><b data-testid="batch-total-multa">+ {brl(totalMulta)}</b>
                    </div>
                  )}
                  {totalJuros > 0 && (
                    <div className="flex justify-between text-amber-800">
                      <span>Juros de atraso</span><b data-testid="batch-total-juros">+ {brl(totalJuros)}</b>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-1">
                    <span className="font-semibold">Total a receber</span>
                    <b data-testid="batch-total">{brl(totalReceber)}</b>
                  </div>
                </div>

                <div className="rounded-xl border p-3 space-y-2">
                  <div>
                    <label className="text-xs font-semibold">Valor efetivamente recebido *</label>
                    <Input type="number" step="0.01" min="0" max={totalReceber} value={valor} data-testid="batch-amount"
                      placeholder={brl(totalReceber)} onChange={(e) => { setValor(e.target.value); setAjuste(false); }} />
                  </div>
                  {parcial && (
                    <div className="rounded-lg bg-amber-50 p-2 text-[11.5px] text-amber-800" data-testid="batch-partial-hint">
                      Pagamento parcial — faltam {brl(falta)}. Distribuímos do vencimento mais antigo para o mais novo.
                      <Button type="button" size="sm" variant="outline" className="mt-1.5 h-7 w-full text-[11px]"
                        data-testid="batch-ajustar-distribuicao"
                        onClick={() => { setManualAlloc(ajuste ? {} : { ...auto }); setAjuste(!ajuste); }}>
                        {ajuste ? 'Voltar ao automático' : 'Ajustar distribuição'}
                      </Button>
                    </div>
                  )}
                  <div className="flex justify-between text-[12.5px]">
                    <span className="font-semibold">Total distribuído</span>
                    <b className={distOk ? 'text-emerald-700' : 'text-rose-700'} data-testid="batch-dist-total">{brl(distTotal)}</b>
                  </div>
                  {!distOk && (
                    <p className="text-[11.5px] font-semibold text-rose-700" data-testid="batch-dist-erro">
                      O total distribuído deve ser igual ao valor recebido ({brl(gross)}).
                    </p>
                  )}
                  <div>
                    <label className="text-xs font-semibold">Data do pagamento *</label>
                    <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="batch-date" />
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {METHODS.map(([k, label]) => (
                      <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`batch-method-${k}`}
                        className={`text-[11.5px] font-semibold rounded-full border px-2.5 py-1 transition-colors ${method === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs font-semibold">Receber em</label>
                    <select className="w-full h-9 border rounded-md px-2 text-sm" value={accountId}
                      onChange={(e) => setAccountId(e.target.value)} data-testid="batch-account">
                      <option value="">Selecione a conta</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.nome}{a.provider ? ` · ${a.provider}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  {['card_credit', 'pix_card'].includes(method) && (
                    <div>
                      <label className="text-xs font-semibold">Parcelas</label>
                      <Input type="number" min="1" max="24" value={form.installments} data-testid="batch-installments"
                        onChange={(e) => set('installments', e.target.value)} />
                    </div>
                  )}
                  {isCard && (
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Bandeira" value={form.brand} onChange={(e) => set('brand', e.target.value)} data-testid="batch-brand" />
                      <Input placeholder="4 últimos" value={form.last4} onChange={(e) => set('last4', e.target.value)} data-testid="batch-last4" />
                      <Input placeholder="NSU" value={form.nsu} onChange={(e) => set('nsu', e.target.value)} data-testid="batch-nsu" />
                      <Input placeholder="Autorização" value={form.authorization_code} onChange={(e) => set('authorization_code', e.target.value)} data-testid="batch-auth" />
                    </div>
                  )}
                  {method === 'cash' && (
                    <div>
                      <label className="text-xs font-semibold">Valor entregue (para troco)</label>
                      <Input type="number" step="0.01" value={form.received_amount} data-testid="batch-received"
                        onChange={(e) => set('received_amount', e.target.value)} />
                      {form.received_amount > gross && (
                        <p className="text-[11px] text-emerald-700 mt-0.5">Troco: {brl(form.received_amount - gross)}</p>
                      )}
                    </div>
                  )}
                  <Input placeholder="Observação" value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="batch-notes" />
                  <SplitPaymentLines extras={extras} setExtras={setExtras} methods={METHODS}
                    line1={{ amount: line1Gross, fee: fee1 }} devido={totalReceber} addLabel="Adicionar forma de recebimento" />
                </div>

                <div className="sticky bottom-0 rounded-xl border bg-slate-50 p-3 text-[13px]" data-testid="batch-preview">
                  {split && (
                    <div className="mb-1 space-y-0.5 border-b pb-1" data-testid="batch-split-resumo">
                      <div className="flex justify-between"><span>Total devido</span><b>{brl(totalReceber)}</b></div>
                      <div className="flex justify-between"><span>Distribuído nos títulos</span><b data-testid="batch-split-dist">{brl(distTotal)}</b></div>
                      <div className="flex justify-between"><span>Restante</span><b className={totalReceber - gross > 0.009 ? 'text-amber-700' : 'text-emerald-700'} data-testid="batch-split-restante">{brl(Math.max(0, r2(totalReceber - gross)))}</b></div>
                      <p className="pt-1 text-[10px] font-bold uppercase text-slate-500">Formas</p>
                      {[{ method, amount: line1Gross, fee: fee1 }, ...extras.map((l) => ({ method: l.method, amount: r2(l.amount), fee: lineFee(l) }))].map((f, i) => (
                        <div key={i} className="flex justify-between text-[12px]" data-testid={`batch-split-forma-${i + 1}`}>
                          <span>{(METHODS.find(([k]) => k === f.method) || [0, f.method])[1]}</span>
                          <span>{brl(f.amount)}{f.fee > 0 ? ` · taxa ${brl(f.fee)}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between"><span>Valor recebido</span><b data-testid="batch-gross">{brl(gross)}</b></div>
                  <div className="flex justify-between text-amber-800">
                    <span>{split ? 'Taxas' : `Taxa ${quote?.provider || ''}${quote?.fee_percent ? ` (${quote.fee_percent}%)` : ''}`}</span>
                    <b>- {brl(fee)}</b>
                  </div>
                  <div className="flex justify-between border-t mt-1 pt-1">
                    <span className="font-semibold">Valor líquido</span><b data-testid="batch-net">{brl(gross - fee)}</b>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    A taxa é custo da escola: os títulos recebem {brl(gross)}.
                  </p>
                </div>

                <Button onClick={submit} data-testid="batch-confirm" disabled={bloqueado}
                  className="w-full bg-emerald-600 hover:bg-emerald-700">
                  {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Confirmar recebimento de {brl(gross)}
                </Button>
                <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>Cancelar</Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
